import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';

export interface OrgTheme {
  primaryColor: string;
  logoUrl: string;
  orgName: string;
}

/** Default Chareety blue when no org theme is configured */
const DEFAULT_PRIMARY = '#2B7CB8';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private themeSubject = new BehaviorSubject<OrgTheme>({
    primaryColor: DEFAULT_PRIMARY,
    logoUrl: '',
    orgName: '',
  });

  /** Observable of the current org theme — available before login */
  readonly theme$ = this.themeSubject.asObservable();

  /** Synchronous snapshot */
  get theme(): OrgTheme { return this.themeSubject.value; }

  constructor(
    private afs: AngularFirestore,
    private storage: AngularFireStorage,
  ) {}

  /**
   * Load org theme from Firestore. Each spoke has exactly one Organization doc.
   * This collection is readable without auth (public org info).
   * Called once at app init from AppComponent.
   */
  async initialize(): Promise<void> {
    try {
      const snapshot = await this.afs
        .collection('Organizations', ref => ref.limit(1))
        .get()
        .pipe(first())
        .toPromise();

      if (snapshot && !snapshot.empty) {
        const doc = snapshot.docs[0].data() as any;
        const primaryColor = doc.ThemeColor || DEFAULT_PRIMARY;
        const orgName = doc.Name || '';

        // Resolve logo: LogoLocation is a gs:// Storage path, LogoUrl is a direct URL
        let logoUrl = '';
        if (doc.LogoLocation) {
          try {
            logoUrl = await this.storage
              .refFromURL(doc.LogoLocation)
              .getDownloadURL()
              .toPromise() ?? '';
          } catch {
            logoUrl = '';
          }
        } else if (doc.LogoUrl) {
          logoUrl = doc.LogoUrl;
        }

        this.themeSubject.next({ primaryColor, logoUrl, orgName });
        this.applyTheme(primaryColor);
      }
    } catch (err) {
      // Silently fall back to defaults — theme is cosmetic
      console.warn('ThemeService: could not load org theme, using defaults', err);
    }
  }

  /**
   * Apply a primary color as CSS custom property overrides on :root.
   * Generates light, bg, and dark variants programmatically.
   */
  private applyTheme(hex: string): void {
    const root = document.documentElement.style;
    const dark = this.darken(hex, 0.25);
    const light = this.tint(hex, 0.75);
    const bg = this.tint(hex, 0.92);

    // ── Chareety custom properties ────────────────────────────────────
    root.setProperty('--chareety-primary', hex);
    root.setProperty('--chareety-primary-dark', dark);
    root.setProperty('--chareety-primary-light', light);
    root.setProperty('--chareety-primary-bg', bg);

    // ── Material MDC overrides (runtime theme swap) ───────────────────
    // Filled buttons (mat-flat-button color="primary")
    root.setProperty('--mdc-filled-button-container-color', hex);
    root.setProperty('--mdc-filled-button-label-text-color', '#fff');

    // Outlined / text buttons
    root.setProperty('--mdc-text-button-label-text-color', hex);
    root.setProperty('--mdc-outlined-button-label-text-color', hex);
    root.setProperty('--mdc-outlined-button-outline-color', hex);

    // Protected (raised) buttons
    root.setProperty('--mdc-protected-button-label-text-color', hex);

    // FAB
    root.setProperty('--mdc-fab-container-color', hex);

    // Form field focus ring & label
    root.setProperty('--mdc-outlined-text-field-focus-outline-color', hex);
    root.setProperty('--mdc-outlined-text-field-focus-label-text-color', hex);
    root.setProperty('--mdc-outlined-text-field-caret-color', hex);
    root.setProperty('--mdc-filled-text-field-focus-active-indicator-color', hex);
    root.setProperty('--mdc-filled-text-field-focus-label-text-color', hex);
    root.setProperty('--mdc-filled-text-field-caret-color', hex);

    // Floating label
    root.setProperty('--mat-form-field-focus-select-arrow-color', hex);

    // Checkbox
    root.setProperty('--mdc-checkbox-selected-checkmark-color', '#fff');
    root.setProperty('--mdc-checkbox-selected-focus-icon-color', hex);
    root.setProperty('--mdc-checkbox-selected-hover-icon-color', hex);
    root.setProperty('--mdc-checkbox-selected-icon-color', hex);
    root.setProperty('--mdc-checkbox-selected-pressed-icon-color', hex);
    root.setProperty('--mdc-checkbox-selected-focus-state-layer-color', hex);
    root.setProperty('--mdc-checkbox-selected-hover-state-layer-color', hex);

    // Radio button
    root.setProperty('--mdc-radio-selected-focus-icon-color', hex);
    root.setProperty('--mdc-radio-selected-hover-icon-color', hex);
    root.setProperty('--mdc-radio-selected-icon-color', hex);
    root.setProperty('--mdc-radio-selected-pressed-icon-color', hex);

    // Slide toggle
    root.setProperty('--mdc-switch-selected-handle-color', hex);
    root.setProperty('--mdc-switch-selected-track-color', light);
    root.setProperty('--mdc-switch-selected-focus-handle-color', dark);
    root.setProperty('--mdc-switch-selected-hover-handle-color', dark);
    root.setProperty('--mdc-switch-selected-pressed-handle-color', dark);

    // Tabs
    root.setProperty('--mdc-tab-indicator-active-indicator-color', hex);
    root.setProperty('--mat-tab-header-active-label-text-color', hex);
    root.setProperty('--mat-tab-header-active-ripple-color', hex);
    root.setProperty('--mat-tab-header-active-focus-label-text-color', hex);
    root.setProperty('--mat-tab-header-active-hover-label-text-color', hex);

    // Stepper
    root.setProperty('--mat-stepper-header-selected-state-icon-background-color', hex);
    root.setProperty('--mat-stepper-header-edit-state-icon-background-color', hex);

    // Spinner / progress
    root.setProperty('--mdc-circular-progress-active-indicator-color', hex);
    root.setProperty('--mdc-linear-progress-active-indicator-color', hex);

    // Select / option
    root.setProperty('--mat-option-selected-state-label-text-color', hex);
    root.setProperty('--mat-minimal-pseudo-checkbox-selected-checkmark-color', hex);
    root.setProperty('--mat-full-pseudo-checkbox-selected-icon-color', hex);

    // Icon buttons (primary colored)
    root.setProperty('--mat-icon-button-state-layer-color', hex);
  }

  // ── Color math helpers ──────────────────────────────────────────────

  /** Parse hex (#RRGGBB) to { r, g, b } */
  private hexToRgb(hex: string): { r: number; g: number; b: number } {
    const h = hex.replace('#', '');
    return {
      r: parseInt(h.substring(0, 2), 16),
      g: parseInt(h.substring(2, 4), 16),
      b: parseInt(h.substring(4, 6), 16),
    };
  }

  /** Convert { r, g, b } back to #RRGGBB */
  private rgbToHex(r: number, g: number, b: number): string {
    const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
    return '#' + [r, g, b].map(v => clamp(v).toString(16).padStart(2, '0')).join('');
  }

  /** Mix with white by a factor (0 = original, 1 = white) */
  private tint(hex: string, factor: number): string {
    const { r, g, b } = this.hexToRgb(hex);
    return this.rgbToHex(
      r + (255 - r) * factor,
      g + (255 - g) * factor,
      b + (255 - b) * factor,
    );
  }

  /** Darken by a factor (0 = original, 1 = black) */
  private darken(hex: string, factor: number): string {
    const { r, g, b } = this.hexToRgb(hex);
    return this.rgbToHex(
      r * (1 - factor),
      g * (1 - factor),
      b * (1 - factor),
    );
  }
}
